<script setup lang="ts">
/**
 * The KPI board (#898): each team's key KPI as a light with its trend and when it was last read, and, where
 * `includeCompany` asks for them, the company's own KPIs ahead of the teams. A page that already shows the company
 * KPIs in full (Enterprise's Company KPIs card) leaves them out, so no KPI is listed twice on one page.
 *
 * The page reads the board (`api.kpiBoard`) and hands it in: it decides when to read it again, and says it could
 * not read it through `error`. A board that cannot be read never draws as an all-clear one.
 */
import { NO_KEY_KPI } from '@zerohuman/shared'
import { sortKpiViews, ZhKpiLight, ZhPanel, ZhSectionHeading } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { KpiBoard, KpiRag, KpiReadingView } from '@/api'

const props = defineProps<{
    /** `null` until the board is read, and while a read fails: an empty board is not "all green". */
    board: KpiBoard | null
    error: string | null
    /** List the company's own KPIs ahead of the teams' key KPIs. */
    includeCompany: boolean
}>()

/**
 * One line of the board: a company KPI, or a team and its key KPI. A team's line carries its id, so its light is
 * one click from its page and a task KPI two from the board.
 */
interface KpiBoardRow {
    id: string
    /** The KPI's own name for a company KPI, the team's name for a team's line. */
    name: string
    /** `null` where there is no light to draw: no key KPI set, or a KPI nobody has read yet. */
    rag: KpiRag | null
    overdue: boolean
    /** Set on a team's line only; a company KPI belongs to no team page. */
    teamId: string | null
    /** `null` where a team has set no key KPI: the line says so rather than drawing a light. */
    reading: KpiReadingView | null
}

/** Trends read as words, so the arrow is never the only channel. */
const TREND_WORD: Readonly<Record<KpiReadingView['trend'], string>> = {
    up: '↑ rising',
    down: '↓ falling',
    flat: '→ flat',
}

/**
 * The company's own KPIs first (when included), then each team's key KPI. Within each, AMBER/RED/overdue first and
 * ties by name (`sortKpiViews`). A team that has set no key KPI sorts with those wanting attention — nothing to
 * read is as worth seeing as a stale reading — and draws no light, because no light is the honest answer to a
 * KPI nobody set.
 */
const rows = computed<KpiBoardRow[]>(() => {
    const board = props.board
    if (!board) {
        return []
    }
    const company: KpiBoardRow[] = props.includeCompany
        ? board.enterprise.map(reading => ({
              id: `kpi-${reading.id}`,
              name: reading.name,
              rag: reading.rag,
              overdue: reading.overdue,
              teamId: null,
              reading,
          }))
        : []
    const teamRows: KpiBoardRow[] = board.teams.map(team => ({
        id: `team-${team.teamId}`,
        name: team.teamName,
        rag: team.key?.rag ?? null,
        overdue: team.key?.overdue ?? true,
        teamId: team.teamId,
        reading: team.key,
    }))
    return [...sortKpiViews(company), ...sortKpiViews(teamRows)]
})

/** When a reading was last taken, or that it never was — an unread KPI is a gap, not a blank. */
function lastRead(reading: KpiReadingView): string {
    if (!reading.lastReadAt) {
        return 'never read'
    }
    return `read ${new Date(reading.lastReadAt).toLocaleDateString()}`
}
</script>

<template>
	<ZhPanel data-testid="kpi-board" class="space-y-2" aria-labelledby="kpi-board-heading">
		<ZhSectionHeading id="kpi-board-heading">KPIs</ZhSectionHeading>
		<p class="text-muted-foreground text-xs">
			<template v-if="includeCompany">
				The company's own KPIs, then each team's key KPI. Within each, at risk, off target and overdue come
				first.
			</template>
			<template v-else>Each team's key KPI, with at risk, off target and overdue first.</template>
			Open a team for the rest of its KPIs, and the role and task KPIs beneath them.
		</p>
		<p v-if="error" class="text-destructive text-sm" role="alert">{{ error }}</p>
		<ul v-if="rows.length" class="space-y-1">
			<li
				v-for="row in rows"
				:key="row.id"
				:data-testid="`kpi-${row.id}`"
				class="flex flex-wrap items-center gap-2 text-sm"
			>
				<ZhKpiLight v-if="row.reading?.rag" :rag="row.reading.rag" />
				<RouterLink
					v-if="row.teamId"
					class="font-medium underline-offset-2 hover:underline"
					:to="`/teams/${row.teamId}`"
				>
					{{ row.name }}
				</RouterLink>
				<span v-else class="font-medium">{{ row.name }}</span>
				<span v-if="row.reading" class="text-muted-foreground text-xs">
					<template v-if="row.teamId">{{ row.reading.name }} · </template>
					<template v-if="row.reading.lastReadAt">{{ TREND_WORD[row.reading.trend] }} · </template>
					{{ lastRead(row.reading) }}
					<template v-if="row.reading.overdue"> · overdue</template>
				</span>
				<span v-else class="text-muted-foreground text-xs">{{ NO_KEY_KPI }}</span>
			</li>
		</ul>
		<p v-else-if="!error" class="text-muted-foreground text-sm">
			{{ includeCompany ? 'No KPIs yet.' : 'No team KPIs yet.' }}
		</p>
	</ZhPanel>
</template>
